import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ManageConsultants.css";
import { DeleteAndGetResponse, ExecuteApiToPost, UpdateAndGetResponse, GetDataFromApiWithParams } from "../../../../../../services/api/ExecuteApiRequests";
import { 
  Box, 
  Button, 
  Alert, 
  CircularProgress, 
  Typography, 
  Card, 
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Modal,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from "@mui/material";
import { Add, Edit, Delete, Save, Cancel, Download, Close } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ManageContractors = () => {
  const [contractors, setContractors] = useState([]);
  const [editingContractor, setEditingContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Countries list
  const countries = [
    "Ethiopia", "United States", "United Kingdom", "China", "India", 
    "UAE", "Turkey", "Italy", "Germany", "France", "Canada", "Australia"
  ];

  // Contractor grades
  const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Contractor types
  const contractorTypes = [
    "ElectroMechnical contractor",
    "Building Contractor",
    "Road Contractor",
    "Water Works Contractor",
    "Electrical Contractor",
    "Mechanical Contractor",
    "General Contractor"
  ];

  const fetchContractors = async () => {
    try {
      setLoading(true);
      setError("");
      const responseData = await GetDataFromApiWithParams('contractors');
      console.log("Fetched contractors:", responseData);
      setContractors(responseData);
    } catch (error) {
      console.error("Error fetching contractors:", error);
      toast.error("Error fetching contractors: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {    
    fetchContractors();
  }, []);

  const handleEdit = (contractor) => {
    console.log("Editing contractor:", contractor);
    
    setEditingContractor({
      id: contractor.id || "",
      name: contractor.name || "",
      class: contractor.class ? contractor.class.toString() : "1",
      license_no: contractor.license_no || "",
      tin_no: contractor.tin_no || "",
      origin_country: contractor.origin_country || "Ethiopia",
      address_region: contractor.address_region || "",
      address_city: contractor.address_city || "",
      address_subcity: contractor.address_subcity || "",
      address_woreda: contractor.address_woreda || "",
      address_house_no: contractor.address_house_no || "",
      contact_person_name: contractor.contact_person_name || "",
      telephone: contractor.telephone || "",
      email: contractor.email || "",
      description: contractor.description || "",
      contractor_type: contractor.contractor_type || "",
      experience_year: contractor.experience_year || "",
    });
    setEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this contractor?")) {
      try {
        await DeleteAndGetResponse(`contractors/${id}`);
        toast.success("Contractor deleted successfully!");
        fetchContractors();
      } catch (error) {
        console.error("Error deleting contractor:", error);
        toast.error("Error deleting contractor: " + (error.message || "Unknown error"));
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingContractor) return;

    console.log("Saving contractor data:", editingContractor);

    if (!editingContractor.name) {
      toast.error("Company name is required");
      return;
    }

    if (!editingContractor.class) {
      toast.error("Class is required");
      return;
    }

    if (!editingContractor.license_no) {
      toast.error("License number is required");
      return;
    }

   

    setSubmitting(true);

    try {
      await UpdateAndGetResponse(`contractors/${editingContractor.id}`, {
        name: editingContractor.name,
        class: editingContractor.class,
        license_no: editingContractor.license_no,
        tin_no: editingContractor.tin_no,
        origin_country: editingContractor.origin_country,
        address_region: editingContractor.address_region,
        address_city: editingContractor.address_city,
        address_subcity: editingContractor.address_subcity,
        address_woreda: editingContractor.address_woreda,
        address_house_no: editingContractor.address_house_no,
        contact_person_name: editingContractor.contact_person_name,
        telephone: editingContractor.telephone,
        email: editingContractor.email,
        description: editingContractor.description,
        contractor_type: editingContractor.contractor_type,
        experience_year: editingContractor.experience_year,
        id: editingContractor.id,
      });
      
      toast.success("Contractor updated successfully!");
      setEditModalOpen(false);
      setEditingContractor(null);
      fetchContractors();
    } catch (error) {
      console.error("Error updating contractor:", error);
      toast.error("Error updating contractor: " + (error.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingContractor(null);
    setEditModalOpen(false);
  };

  const handleExportAll = () => {
    try {
      if (contractors.length === 0) {
        toast.error("No data to export");
        return;
      }

      const headers = [
        'ID', 'Company Name', 'Class', 'Contractor Type', 'Years of Experience',
        'License No', 'TIN No', 'Origin Country', 'Address Region', 'City', 
        'Sub-City', 'Woreda', 'House No', 'Contact Person', 'Telephone', 
        'Email', 'Description'
      ];
      
      const csvHeaders = headers.join(',');
      
      const csvRows = contractors.map(contractor => {
        const row = [
          contractor.id,
          `"${contractor.name || ''}"`,
          contractor.class,
          `"${contractor.contractor_type || ''}"`,
          contractor.years_of_experience || '',
          contractor.license_no,
          contractor.tin_no,
          contractor.origin_country,
          contractor.address_region,
          contractor.address_city,
          contractor.address_subcity,
          contractor.address_woreda,
          contractor.address_house_no,
          `"${contractor.contact_person_name || ''}"`,
          contractor.telephone,
          contractor.email,
          `"${contractor.description || ''}"`
        ];
        return row.join(',');
      }).join('\n');
      
      const csvContent = `${csvHeaders}\n${csvRows}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `contractors_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${contractors.length} contractors successfully!`);
    } catch (err) {
      console.error("Error exporting to CSV:", err);
      toast.error("Error exporting contractors data");
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1200,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    overflow: 'auto',
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading contractors...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Manage Contractors ({contractors.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportAll}
                disabled={contractors.length === 0}
              >
                Export All
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                component={Link}
                to="/registerContractor"
              >
                New Contractor
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {contractors.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Grades</TableCell>
                    <TableCell>Contractor Type</TableCell>
                    <TableCell>Years Exp</TableCell>
                    <TableCell>License No</TableCell>
                    <TableCell>TIN No</TableCell>
                    <TableCell>Origin Country</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Sub-City</TableCell>
                    <TableCell>Woreda</TableCell>
                    <TableCell>House No</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Telephone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contractors.map((contractor) => (
                    <TableRow key={contractor.id} hover>
                      <TableCell>{contractor.id}</TableCell>
                      <TableCell>{contractor.name}</TableCell>
                      <TableCell> {contractor.class} </TableCell>
                      <TableCell>{contractor.contractor_type || '-'}</TableCell>
                      <TableCell>{contractor.experience_year || '-'}</TableCell>
                      <TableCell>{contractor.license_no}</TableCell>
                      <TableCell>{contractor.tin_no}</TableCell>
                      <TableCell>{contractor.origin_country}</TableCell>
                      <TableCell>{contractor.address_region}</TableCell>
                      <TableCell>{contractor.address_city}</TableCell>
                      <TableCell>{contractor.address_subcity}</TableCell>
                      <TableCell>{contractor.address_woreda}</TableCell>
                      <TableCell>{contractor.address_house_no}</TableCell>
                      <TableCell>{contractor.contact_person_name}</TableCell>
                      <TableCell>{contractor.telephone}</TableCell>
                      <TableCell>{contractor.email}</TableCell>
                      <TableCell sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                        {contractor.description}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(contractor)}
                            title="Edit"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(contractor.id)}
                            title="Delete"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No contractors found.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Click "New Contractor" to add your first contractor.
                </Typography>
              </Box>
            )
          )}
        </CardContent>
      </Card>

      
     {/* Edit Modal */}
<Modal
  open={editModalOpen}
  onClose={handleCancelEdit}
  aria-labelledby="edit-contractor-modal"
>
  <Box sx={modalStyle}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" component="h2">
        Edit Contractor (ID: {editingContractor?.id})
      </Typography>
      <IconButton onClick={handleCancelEdit} size="small">
        <Close />
      </IconButton>
    </Box>
    
    {editingContractor && (
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
        <Grid container spacing={2}>
          {/* Row 1: Company Name, Class, Contractor Type */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Company Name"
              value={editingContractor.name || ''}
              onChange={(e) => setEditingContractor({...editingContractor, name: e.target.value})}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Contractor Grade</InputLabel>
              <Select
                value={editingContractor.class || ''}
                onChange={(e) => setEditingContractor({...editingContractor, class: e.target.value})}
                label="Contractor Grade"
              >
                {grades.map((grade) => (
                  <MenuItem key={grade} value={grade.toString()}>
                    Grade {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Contractor Type</InputLabel>
              <Select
                value={editingContractor.contractor_type || ''}
                onChange={(e) => setEditingContractor({...editingContractor, contractor_type: e.target.value})}
                label="Contractor Type"
              >
                {contractorTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 2: Years of Experience, License No, TIN No */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Years of Experience"
              type="number"
              value={editingContractor.experience_year || ''}
              onChange={(e) => setEditingContractor({...editingContractor, experience_year: e.target.value})}
              size="small"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="License No"
              value={editingContractor.license_no || ''}
              onChange={(e) => setEditingContractor({...editingContractor, license_no: e.target.value})}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="TIN No"
              value={editingContractor.tin_no || ''}
              onChange={(e) => setEditingContractor({...editingContractor, tin_no: e.target.value})}
              size="small"
            />
          </Grid>

          {/* Row 3: Origin Country (now TextField), Telephone, Email */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Origin Country"
              value={editingContractor.origin_country || ''}
              onChange={(e) => setEditingContractor({...editingContractor, origin_country: e.target.value})}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Telephone"
              value={editingContractor.telephone || ''}
              onChange={(e) => setEditingContractor({...editingContractor, telephone: e.target.value})}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editingContractor.email || ''}
              onChange={(e) => setEditingContractor({...editingContractor, email: e.target.value})}
              size="small"
            />
          </Grid>

          {/* Row 4: Contact Person, Region, City */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact Person Name"
              value={editingContractor.contact_person_name || ''}
              onChange={(e) => setEditingContractor({...editingContractor, contact_person_name: e.target.value})}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Region"
              value={editingContractor.address_region || ''}
              onChange={(e) => setEditingContractor({...editingContractor, address_region: e.target.value})}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={editingContractor.address_city || ''}
              onChange={(e) => setEditingContractor({...editingContractor, address_city: e.target.value})}
              size="small"
            />
          </Grid>

          {/* Row 5: Sub City, Woreda, House No */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Sub City"
              value={editingContractor.address_subcity || ''}
              onChange={(e) => setEditingContractor({...editingContractor, address_subcity: e.target.value})}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Woreda"
              value={editingContractor.address_woreda || ''}
              onChange={(e) => setEditingContractor({...editingContractor, address_woreda: e.target.value})}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="House No"
              value={editingContractor.address_house_no || ''}
              onChange={(e) => setEditingContractor({...editingContractor, address_house_no: e.target.value})}
              size="small"
              required
            />
          </Grid>

          {/* Row 6: Description - Full Width */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={editingContractor.description || ''}
              onChange={(e) => setEditingContractor({...editingContractor, description: e.target.value})}
              multiline
              rows={3}
              size="small"
            />
          </Grid>

          {/* Loading Indicator */}
          {submitting && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2">Updating contractor...</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button 
            onClick={handleCancelEdit} 
            variant="outlined"
            startIcon={<Cancel />}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<Save />}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </Box>
      </Box>
    )}
  </Box>
</Modal>
    </Box>
  );
};

export default ManageContractors;